/**
 * Governed coding-agent dispatch — real worker in, reviewable + reversible
 * file-patch out.
 *
 * This closes the headline auditor findings on dev dispatch:
 *
 *   1. REAL WORKER  — `makeArgoWorker` wraps the injected argoWorker client
 *      (src/lib/agent/argoWorker.ts) so a configured deployment runs the
 *      actual Claude pipeline; `makeFallbackWorker` is the deterministic
 *      in-app stand-in when no worker is configured. Same seam, one-flag
 *      swap, no new config.
 *   2. REAL DIFF    — the worker returns whole-file FilePatches, and
 *      `patchToActionDiff` renders per-file hunks (added/removed lines)
 *      for the approval queue — an actual patch, not a text summary.
 *   3. SCOPE FIRST  — `dispatchTicket` enforces `scopeViolations` BEFORE
 *      any diff or bundle is produced. A patch that escapes its blast
 *      radius yields ok=false, diff=null, bundle=null — nothing reviewable
 *      ever exists for an out-of-scope change.
 *   4. REVERSIBLE   — `reversibleBundle` pairs the apply patch with its
 *      exact inverse (before/after swapped), sealed-ready for the
 *      Decision Ledger. Reverting restores original content byte-for-byte.
 *
 * Pure except for the injected worker; no store, no firebase, no fetch of
 * its own.
 */

import type { BlastScope, DispatchContract, FilePatch } from './dispatchContract';
import { changedLineCount, scopeViolations } from './dispatchContract';

// ---------------------------------------------------------------------------
// Worker seam
// ---------------------------------------------------------------------------

/** The one seam a dispatch runs through: contract in, patch + summary out. */
export interface Worker {
  run(contract: DispatchContract): Promise<{ patch: FilePatch[]; summary: string }>;
}

/**
 * The slice of the argoWorker module a dispatch needs — injected, never
 * imported here, so tests can hand in a double and the UI hands in the
 * real module. Matches `runAgent` in src/lib/agent/argoWorker.ts exactly.
 */
export interface ArgoWorkerClient {
  runAgent(
    agent: { id: string; name: string; template: string; persona: string },
    snapshot: unknown,
  ): Promise<{ text: string }>;
}

/** The agent descriptor the coding dispatch runs as on the worker. */
const CODING_AGENT = {
  id: 'dev-dispatch',
  name: 'Dev Dispatch',
  template: 'coding-agent',
  persona:
    'You are a governed coding agent. Given a DispatchContract (goal + blast scope), ' +
    'return STRICT JSON: {"summary": string, "patch": [{"path": string, "before": string, "after": string}]}. ' +
    'Never touch files outside scope.paths, never exceed scope.maxFiles or scope.maxDiffLines.',
};

/** Extract the first JSON object from worker text (tolerates ```json fences). */
function parseWorkerPayload(text: string): { patch: FilePatch[]; summary: string } {
  const fenced = /```(?:json)?\s*([\s\S]*?)```/.exec(text);
  const raw = (fenced ? fenced[1] : text).trim();
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start === -1 || end <= start) {
    throw new Error('Worker returned no JSON patch payload.');
  }
  const parsed = JSON.parse(raw.slice(start, end + 1)) as {
    patch?: unknown;
    summary?: unknown;
  };
  const patch = Array.isArray(parsed.patch)
    ? parsed.patch.filter(
        (f): f is FilePatch =>
          typeof f === 'object' &&
          f !== null &&
          typeof (f as FilePatch).path === 'string' &&
          typeof (f as FilePatch).before === 'string' &&
          typeof (f as FilePatch).after === 'string',
      )
    : [];
  return {
    patch,
    summary: typeof parsed.summary === 'string' ? parsed.summary : '',
  };
}

/**
 * Wrap the injected argoWorker client as a dispatch Worker. Delegates to
 * the real Claude pipeline via `runAgent` and surfaces the patch it
 * returns. No new config: configuration lives entirely in argoWorker.
 */
export function makeArgoWorker(client: ArgoWorkerClient): Worker {
  return {
    async run(contract: DispatchContract) {
      const { text } = await client.runAgent(CODING_AGENT, { contract });
      return parseWorkerPayload(text);
    },
  };
}

/**
 * Deterministic fallback when no worker is configured: emits one honest
 * stub patch INSIDE the contract's scope — a work-plan file under the
 * first allowed path, one file, tiny diff — so the full govern → review →
 * approve → revert loop is exercisable offline. Same contract → same
 * patch, always.
 */
export function makeFallbackWorker(): Worker {
  return {
    async run(contract: DispatchContract) {
      const root = contract.scope.paths[0] ?? 'src';
      const dir = root.endsWith('/') ? root.slice(0, -1) : root;
      const path = `${dir}/DISPATCH-${contract.ticketId}.md`;
      const after = [
        `# Dispatch plan — ${contract.ticketId}`,
        '',
        `Goal: ${contract.goal}`,
        `Tests: ${contract.testsMustPass.join(', ')}`,
        '',
        '(Deterministic fallback patch — no agent worker configured.)',
        '',
      ].join('\n');
      return {
        patch: [{ path, before: '', after }],
        summary: `Fallback plan for ${contract.ticketId}: ${contract.goal}`,
      };
    },
  };
}

// ---------------------------------------------------------------------------
// Patch → reviewable actionDiff
// ---------------------------------------------------------------------------

/** One reviewable hunk: the changed region of one file, old vs new lines. */
export type DiffHunk = {
  path: string;
  /** 1-based line where the change starts (in the before-content). */
  startLine: number;
  removed: string[];
  added: string[];
};

/** The approval-queue payload: per-file hunks + totals. */
export type PatchActionDiff = {
  kind: 'file_patch';
  files: Array<{ path: string; hunk: DiffHunk; changedLines: number }>;
  totalFiles: number;
  totalChangedLines: number;
};

function splitLines(s: string): string[] {
  return s === '' ? [] : s.split('\n');
}

/** The changed region of one file after trimming common prefix/suffix. */
function hunkFor(file: FilePatch): DiffHunk {
  const a = splitLines(file.before);
  const b = splitLines(file.after);
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }
  return {
    path: file.path,
    startLine: start + 1,
    removed: a.slice(start, endA),
    added: b.slice(start, endB),
  };
}

/**
 * Render a patch as an ACTUAL per-file-hunk actionDiff for approval-queue
 * review — exactly one hunk per file, with the old lines the change removes
 * and the new lines it adds. This is what the operator approves: the patch
 * itself, never a prose summary.
 */
export function patchToActionDiff(patch: FilePatch[]): PatchActionDiff {
  const files = patch.map((f) => ({
    path: f.path,
    hunk: hunkFor(f),
    changedLines: changedLineCount(f),
  }));
  return {
    kind: 'file_patch',
    files,
    totalFiles: files.length,
    totalChangedLines: files.reduce((s, f) => s + f.changedLines, 0),
  };
}

// ---------------------------------------------------------------------------
// Reversible bundle
// ---------------------------------------------------------------------------

/** One sealed-ready patch action: what gets written and by whom. */
export type PatchAction = {
  kind: 'apply_file_patch';
  actor: string;
  files: FilePatch[];
};

/**
 * Pair the apply patch with its EXACT inverse: revert swaps before/after
 * per file, so applying revert after apply restores the original content
 * byte-for-byte. Both halves are plain JSON — sealed-ready for the
 * Decision Ledger — and the classifier (reversibility.ts) can treat an
 * applied bundle as fully reversible because its inverse ships with it.
 */
export function reversibleBundle(
  patch: FilePatch[],
  actor: string,
): { apply: PatchAction; revert: PatchAction } {
  return {
    apply: {
      kind: 'apply_file_patch',
      actor,
      files: patch.map((f) => ({ path: f.path, before: f.before, after: f.after })),
    },
    revert: {
      kind: 'apply_file_patch',
      actor,
      files: patch.map((f) => ({ path: f.path, before: f.after, after: f.before })),
    },
  };
}

// ---------------------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------------------

export type DispatchResult = {
  ok: boolean;
  /** Reviewable per-file-hunk diff — null the moment scope is violated. */
  diff: PatchActionDiff | null;
  violations: string[];
  /** Sealed-ready apply/revert pair — null unless the patch is in scope. */
  bundle: { apply: PatchAction; revert: PatchAction } | null;
  summary: string;
};

/**
 * Run one governed dispatch: worker → scope gate → diff + bundle.
 *
 * Blast-radius enforcement comes FIRST: if the returned patch escapes
 * `contract.scope`, the result is ok=false with every violation named and
 * diff=null / bundle=null — no reviewable artifact is ever produced for an
 * out-of-scope change. A worker failure is reported the same honest way,
 * never swallowed into a fake success.
 */
export async function dispatchTicket(
  worker: Worker,
  contract: DispatchContract,
): Promise<DispatchResult> {
  let patch: FilePatch[];
  let summary: string;
  try {
    const out = await worker.run(contract);
    patch = out.patch;
    summary = out.summary;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { ok: false, diff: null, violations: [`worker failed: ${msg}`], bundle: null, summary: '' };
  }

  const violations = scopeViolations(patch, contract.scope);
  if (violations.length > 0) {
    return { ok: false, diff: null, violations, bundle: null, summary };
  }

  return {
    ok: true,
    diff: patchToActionDiff(patch),
    violations: [],
    bundle: reversibleBundle(patch, 'argo-dev-agent'),
    summary,
  };
}

export type { BlastScope, DispatchContract, FilePatch };
